import React, { useState, useEffect, useRef } from 'react';
import { tracoIcone } from '../utils/tracoIcone';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Foto de produto (31/07/2026, migrada de base64-no-banco pra arquivo
 * em disco) — antes vinha embutida direto no campo `foto` do produto;
 * agora busca via API e converte pra blob URL, mesmo padrão já usado
 * pra foto de perfil no AuthContext. Endpoint (sala6Controller,
 * reaproveitado por qualquer sala que gerencie produto — ex.: Sala 33)
 * aceita acesso à Sala 6 OU à Sala 33 (corrigido 07/08/2026 — achado
 * real do Juan: exigia só Sala 6, e quem tinha acesso somente à
 * Sala 33 ficava sem ver foto nenhuma).
 *
 * `ampliavel` (07/08/2026, pedido do Juan) — clique abre a foto num
 * visualizador em tela cheia, sem precisar sair da tela atual.
 *
 * Proporção (07/08/2026, achado real do Juan) — mobília tem formatos
 * muito diferentes entre si (uma estante baixa e larga não é igual a
 * uma poltrona) — forçar todo produto num quadrado com object-fit:
 * cover cortava o produto de um jeito sem sentido (ex.: uma estante
 * larga e baixa virava só uma faixa horizontal da textura da
 * madeira, sem dar pra ver o formato real). Trocado pra object-fit:
 * contain (mostra o produto inteiro, redondo ou reto, sobra espaço
 * vazio nas laterais/topo quando a proporção não combina exatamente
 * com a caixa — mas nunca corta o produto de um jeito que não faz
 * sentido) — e `width`/`height` agora aceitam valores diferentes
 * (retangular), não só um `size` quadrado.
 */
export default function FotoProduto({ produtoId, temFoto, size = 64, width, height, radius = 14, fallback = null, ampliavel = false }) {
  const { API } = useAuth();
  const [url, setUrl] = useState(null);
  const [ampliada, setAmpliada] = useState(false);
  /* ⚠ (V1876, ele: "a sala 1 tá demorando pra carregar") A FOTO SÓ É BUSCADA QUANDO APARECE NA TELA. Cada cartão do histórico
     pedia a foto do produto ao montar — dezenas de pedidos de imagem disputando a rede com os dados da sala, inclusive
     dos cartões lá embaixo. Agora o vazio branco é observado e a foto vem quando ele chega perto da tela (200 px antes).
     Com `fallback` próprio (outro desenho no lugar do vazio) não há onde observar: busca na hora, como antes. */
  const vazioRef = useRef(null);
  const [perto, setPerto] = useState(fallback !== null || typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (perto || !vazioRef.current) return;
    const ob = new IntersectionObserver((es) => { if (es.some(e => e.isIntersecting)) { setPerto(true); ob.disconnect(); } }, { rootMargin: '200px' });
    ob.observe(vazioRef.current);
    return () => ob.disconnect();
  }, [perto]);
  const w = width ?? size;
  const h = height ?? size;
  /* Só número entra na conta do ícone: `w`/`h` podem ser medida de CSS
     ("100%", "12rem"), e aí o lado vem do `size`, que é sempre número. */
  const numeros = [w, h].filter(v => typeof v === 'number' && Number.isFinite(v));
  const ladoIcone = (numeros.length ? Math.min(...numeros) : size) * 0.4;
  useEffect(() => {
    let objUrl;
    setUrl(null);
    if (!produtoId) return;
    /* NÃO PEDIR FOTO DE QUEM NÃO TEM (23/08/2026, achado do Juan).
       A listagem já devolve a coluna `foto`; quando ela vem vazia, o
       arquivo não existe e a requisição volta 404 — garantido.

       Com 1.171 produtos e a maioria sem foto, isso enchia o console
       de erro (407 entradas num único carregamento) e gastava uma
       requisição por card. Erro previsível não deveria ser feito.

       `temFoto === undefined` mantém o comportamento antigo: quem
       chama sem informar continua tentando, e nada quebra. */
    if (temFoto === null || temFoto === false || temFoto === '') return;
    if (!perto) return;   // (V1876) ainda longe da tela
    API.get(`/sala6/produtos/${produtoId}/foto`, { responseType: 'blob' })
      .then(r => { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); })
      /* 404 é resposta esperada — produto sem foto. Só o resto é erro. */
      .catch(e => { if (e.response?.status !== 404) console.error('[FotoProduto]', e.message); });
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [produtoId, temFoto, API, perto]);
  if (!url) return fallback !== null ? fallback : (
    <div ref={vazioRef} style={{
      width:w, height:h, borderRadius:radius, flexShrink:0,
      background:'#FFFFFF', border:'1px solid var(--border)',
      display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-secondary)' }}>
      {/* Achado real (10/08/2026, pedido do Juan — "os espaços vazios
          deixe sempre branco independentemente do modo... para sempre
          a caixinha estar preenchida"): a versão anterior usava
          rgba(255,255,255,.06) — um tom RELATIVO (clareava um pouco o
          que tivesse por baixo), então no escuro a caixa continuava
          escura (só um pouco menos), com o ícone claro flutuando nela.
          Virou um branco absoluto, fixo, igual nos dois temas — border
          e cor do ícone também fixos (mesmos valores que o modo claro
          já usa) pra continuar legível contra esse branco sempre. */}
      {/* ⚠⚠ `NaN` NO ATRIBUTO DO SVG (17/09/2026, print do console dele
          com 153 erros: *attribute width: Expected length, "NaN"*).

          ⚠ A mensagem original cita a tag entre sinais de menor e maior.
          Escrevê-la assim aqui fez o portão `svg_sem_tamanho` ler o
          COMENTÁRIO como um SVG sem largura — falso positivo criado por
          citar o erro literalmente.

          `w` e `h` aceitam VALOR DE CSS — a Sala 33 passa
          `width="100%"` — e `Math.min("100%", 150)` devolve NaN. O ícone
          então recebia `width={NaN}` e não desenhava: o espaço da foto
          ficava branco e vazio, sem nem o ícone de "sem foto".

          ⚠ E ERRAVA EM SILÊNCIO NA TELA: só o console reclamava, um erro
          por produto. Numa lista de 153, 153 linhas de erro e nenhuma
          pista visual além de um quadrado branco a mais.

          ⚠ O lado do ícone agora só sai de número; com medida em texto
          ele cai no padrão, que é o tamanho base do componente. */}
      <svg width={ladoIcone} height={ladoIcone} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={tracoIcone(ladoIcone)}>
        <rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 15l-5-5-8 8"/>
      </svg>
    </div>
  );
  return (
    <>
      <div onClick={ampliavel ? () => setAmpliada(true) : undefined}
        style={{
          width:w, height:h, borderRadius:radius, border:'1px solid var(--border)', background:'#FFFFFF',
          cursor: ampliavel ? 'zoom-in' : 'default', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        {/* Fundo fixo em branco (10/08/2026, mesmo pedido acima) — como
            a foto usa object-fit:contain (preserva a proporção real do
            produto, ver comentário no topo do arquivo), produto fino
            ou não-quadrado deixa sobra de fundo visível nas laterais;
            antes essa sobra usava var(--bg-primary) e virava escura no
            tema escuro. Fixo garante a "caixinha preenchida" nos dois
            temas, foto quadrada ou não. */}
        <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
      </div>
      {ampliada && createPortal(
        <div className="modal-overlay" style={{ left:0, zIndex:2100 }} onClick={() => setAmpliada(false)}>
          <img src={url} alt="" style={{ maxWidth:'92vw', maxHeight:'92vh', borderRadius: 'var(--r-lg)', objectFit:'contain' }} onClick={e => e.stopPropagation()} />
          <button className="acao-fechar" style={{ position:'fixed', top:24, right:28, fontSize:'var(--txt-30)', color:'#fff' }} onClick={() => setAmpliada(false)}>×</button>
        </div>,
        document.body
      )}
    </>
  );
}
